import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, ArrowUpDown, ArrowUp, ArrowDown, Pencil, Trash2, Hammer, FileText, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, pct, margeText, margeBadge, STATUT_DEVIS } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { Button } from "../ui/button";
import DevisBuilder from "./DevisBuilder";

export default function DevisView() {
  const { canSeeMargins } = useViewRole();
  const [devis, setDevis] = useState([]);
  const [clients, setClients] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState("Toutes");
  const [statusFilter, setStatusFilter] = useState("Tous");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    api.get("/devis").then((r) => setDevis(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/catalogue").then((r) => setCatalogue(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const id = searchParams.get("open");
    if (!id || !devis.length) return;
    const local = devis.find((d) => String(d.id) === String(id));
    if (local) {
      openEdit(local);
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [devis, searchParams, setSearchParams]);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setOpen(true); };

  const updateEditingDossier = (updated) => {
    if (!updated?.id) return;

    setDevis((current) =>
      current.map((d) => String(d.id) === String(updated.id) ? updated : d)
    );
    setEditing(updated);
  };

  const remove = async (id) => {
    try { await api.delete(`/devis/${id}`); toast.success("Devis supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const convert = async (d) => {
    try {
      await api.post(`/devis/${d.id}/convertir`);
      toast.success(`Devis ${d.numero} validé et converti en chantier`);
      load();
    } catch { toast.error("Erreur lors de la conversion"); }
  };

  const families = useMemo(() => {
    const values = new Set();
    devis.forEach((d) => {
      if (d.famille) values.add(d.famille);
      (d.lignes || []).forEach((l) => l.famille && values.add(l.famille));
    });
    return ["Toutes", ...Array.from(values).sort((a, b) => a.localeCompare(b, "fr"))];
  }, [devis]);

  const statuses = useMemo(() => ["Tous", ...Array.from(new Set(devis.map((d) => d.statut).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr"))], [devis]);

  const getFamily = (d) => d.famille || (d.lignes || []).find((l) => l.famille)?.famille || "";
  const getSearchText = (d) => [
    d.numero, d.titre, d.client_nom, d.chantier_nom, d.famille,
    ...(d.lignes || []).flatMap((l) => [l.nom, l.famille])
  ].filter(Boolean).join(" ").toLowerCase();

  const filteredDevis = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = devis.filter((d) =>
      (!q || getSearchText(d).includes(q)) &&
      (familyFilter === "Toutes" || getFamily(d) === familyFilter) &&
      (statusFilter === "Tous" || d.statut === statusFilter)
    );

    return [...rows].sort((a, b) => {
      let av, bv;
      if (sortKey === "client") {
        av = (a.client_nom || a.titre || "").toLowerCase();
        bv = (b.client_nom || b.titre || "").toLowerCase();
      } else if (sortKey === "total") {
        av = Number(a.total_ht || 0); bv = Number(b.total_ht || 0);
      } else if (sortKey === "marge") {
        av = Number(a.marge_brute_ht || 0); bv = Number(b.marge_brute_ht || 0);
      } else if (sortKey === "numero") {
        av = a.numero || ""; bv = b.numero || "";
      } else {
        av = a.created_at || a.date_creation || a.numero || "";
        bv = b.created_at || b.date_creation || b.numero || "";
      }
      const cmp = typeof av === "string" ? av.localeCompare(bv, "fr", { numeric: true }) : av - bv;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [devis, search, familyFilter, statusFilter, sortKey, sortDir]);

  const totals = useMemo(() => ({
    count: filteredDevis.length,
    ht: filteredDevis.reduce((sum, d) => sum + Number(d.total_ht || 0), 0),
    marge: filteredDevis.reduce((sum, d) => sum + Number(d.marge_brute_ht || 0), 0),
  }), [filteredDevis]);

  const setSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir(key === "client" || key === "numero" ? "asc" : "desc");
    }
  };

  const SortButton = ({ label, column }) => (
    <button type="button" onClick={() => setSort(column)} className="inline-flex items-center gap-1 hover:text-slate-300">
      {label}
      {sortKey === column ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
    </button>
  );

  const hasFilters = search || familyFilter !== "Toutes" || statusFilter !== "Tous";
  const resetFilters = () => { setSearch(""); setFamilyFilter("Toutes"); setStatusFilter("Tous"); };

  return (
    <div className="space-y-6" data-testid="devis-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Devis & Chiffrage</h1>
          <p className="text-slate-400 text-sm mt-1">Devis détaillés avec calcul de marge en temps réel.</p>
        </div>
        <Button data-testid="add-devis-button" onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Nouveau devis
        </Button>
      </div>

      <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input data-testid="devis-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher client, n° devis, chantier, famille..."
              className="w-full h-10 rounded-lg bg-[#0B0F17] border border-[#1E293B] pl-9 pr-9 text-sm outline-none focus:border-amber-500/60" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>}
          </div>
          <select data-testid="devis-family-filter" value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)} className="h-10 rounded-lg bg-[#0B0F17] border border-[#1E293B] px-3 text-sm text-slate-300">
            {families.map((f) => <option key={f} value={f}>{f === "Toutes" ? "Toutes les familles" : f}</option>)}
          </select>
          <select data-testid="devis-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-lg bg-[#0B0F17] border border-[#1E293B] px-3 text-sm text-slate-300">
            {statuses.map((st) => <option key={st} value={st}>{st === "Tous" ? "Tous les statuts" : st}</option>)}
          </select>
          {hasFilters && <button onClick={resetFilters} className="h-10 px-3 rounded-lg border border-[#1E293B] text-xs text-slate-400 hover:text-white">Réinitialiser</button>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2 text-slate-500"><ArrowUpDown className="h-3.5 w-3.5" /> Trier par :</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSort("client")} className={`px-2.5 py-1.5 rounded-md border ${sortKey === "client" ? "border-amber-500/50 text-amber-400" : "border-[#1E293B] text-slate-400"}`}>Client A–Z</button>
            <button onClick={() => setSort("total")} className={`px-2.5 py-1.5 rounded-md border ${sortKey === "total" ? "border-amber-500/50 text-amber-400" : "border-[#1E293B] text-slate-400"}`}>Montant HT</button>
            {canSeeMargins && <button onClick={() => setSort("marge")} className={`px-2.5 py-1.5 rounded-md border ${sortKey === "marge" ? "border-amber-500/50 text-amber-400" : "border-[#1E293B] text-slate-400"}`}>Marge</button>}
            <button onClick={() => setSort("date")} className={`px-2.5 py-1.5 rounded-md border ${sortKey === "date" ? "border-amber-500/50 text-amber-400" : "border-[#1E293B] text-slate-400"}`}>Date</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-3 py-1.5 rounded-md bg-[#0B0F17] border border-[#1E293B] text-slate-300"><b className="text-white">{totals.count}</b> dossier{totals.count > 1 ? "s" : ""}</span>
          <span className="px-3 py-1.5 rounded-md bg-[#0B0F17] border border-[#1E293B] text-slate-300"><b className="text-white">{eur(totals.ht)}</b> HT</span>
          {canSeeMargins && <span className="px-3 py-1.5 rounded-md bg-[#0B0F17] border border-[#1E293B] text-slate-300"><b className="text-emerald-400">{eur(totals.marge)}</b> de marge</span>}
        </div>
      </div>

      <div className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1E293B]">
                <th className="px-4 py-3 font-medium">N°</th>
                <th className="px-3 py-3 font-medium"><SortButton label="Titre / Client" column="client" /></th>
                <th className="px-3 py-3 font-medium">Statut</th>
                <th className="px-3 py-3 font-medium text-right"><SortButton label="Total HT" column="total" /></th>
                {canSeeMargins && <th className="px-3 py-3 font-medium text-right"><SortButton label="Marge" column="marge" /></th>}
                <th className="px-3 py-3 font-medium text-right">Total TTC</th>
                <th className="px-3 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filteredDevis.map((d) => (
                <tr key={d.id} className="border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/50" data-testid={`devis-row-${d.numero}`}>
                  <td className="px-4 py-3 font-mono-metric text-xs text-amber-400">{d.numero}</td>
                  <td className="px-3 py-3">
                    <div className="font-medium">{d.titre}</div>
                    <div className="text-xs text-slate-500">{d.client_nom}</div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${STATUT_DEVIS[d.statut] || ""}`}>{d.statut}</span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono-metric font-semibold">{eur(d.total_ht)}</td>
                  {canSeeMargins && (
                    <td className="px-3 py-3 text-right">
                      <div className="font-mono-metric text-emerald-400">{eur(d.marge_brute_ht)}</div>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${margeBadge(d.taux_marge_pct)}`}>{pct(d.taux_marge_pct)}</span>
                    </td>
                  )}
                  <td className="px-3 py-3 text-right font-mono-metric text-slate-300">{eur(d.total_ttc)}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button data-testid={`devis-edit-${d.numero}`} onClick={() => openEdit(d)} className="text-slate-400 hover:text-amber-400 p-1"><Pencil className="h-4 w-4" /></button>
                    {d.statut !== "Validé / Accepté" && (
                      <button data-testid={`devis-convert-${d.numero}`} onClick={() => convert(d)} className="text-slate-400 hover:text-emerald-400 p-1 ml-1" title="Convertir en chantier"><Hammer className="h-4 w-4" /></button>
                    )}
                    <button data-testid={`devis-delete-${d.numero}`} onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-400 p-1 ml-1"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {filteredDevis.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500"><FileText className="h-6 w-6 mx-auto mb-2" />{devis.length === 0 ? "Aucun devis. Créez votre premier chiffrage." : "Aucun devis ne correspond aux filtres."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DevisBuilder
        open={open}
        onOpenChange={setOpen}
        clients={clients}
        catalogue={catalogue}
        devis={editing}
        onSaved={load}
        onDossierUpdated={updateEditingDossier}
      />
    </div>
  );
}
